import { z } from 'zod';
import {
  AssessmentStatusSchema,
  ContentFormatSchema,
  ResponseStatusSchema,
  ResponseTransitionSchema,
  ReviewStateSchema,
} from './enums.js';
import { ClauseSchema, QuestionSchema } from './standards.js';

export const AssessmentSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  standardVersionId: z.string(),
  standardName: z.string().optional(),
  versionLabel: z.string().optional(),
  /** Whether the standard offers a Statement of Applicability (standards.supportsSoa). */
  standardSupportsSoa: z.boolean().optional(),
  name: z.string(),
  status: AssessmentStatusSchema,
  targetDate: z.string().nullable(),
  previousAssessmentId: z.string().nullable(),
  createdBy: z.string(),
  createdAt: z.string(),
  lockedAt: z.string().nullable(),
});
export type Assessment = z.infer<typeof AssessmentSchema>;

export const CreateAssessmentInputSchema = z.object({
  standardVersionId: z.string(),
  name: z.string().min(1).max(300),
  targetDate: z.string().nullable().optional(),
});
export type CreateAssessmentInput = z.infer<typeof CreateAssessmentInputSchema>;

export const UpdateAssessmentInputSchema = z.object({
  name: z.string().min(1).max(300).optional(),
  targetDate: z.string().nullable().optional(),
  status: z.enum(['open', 'archived']).optional(),
});
export type UpdateAssessmentInput = z.infer<typeof UpdateAssessmentInputSchema>;

export const ProgressSummarySchema = z.object({
  assessmentId: z.string(),
  total: z.number().int(),
  byStatus: z.record(ResponseStatusSchema, z.number().int()),
  completionPercent: z.number(),
  evidenceMissing: z.number().int(),
  openReviewerComments: z.number().int(),
  openAssessorComments: z.number().int(),
  exportReady: z.boolean(),
});
export type ProgressSummary = z.infer<typeof ProgressSummarySchema>;

export const AssessmentDetailSchema = AssessmentSchema.extend({
  progress: ProgressSummarySchema,
});
export type AssessmentDetail = z.infer<typeof AssessmentDetailSchema>;

export const ResponseRecordSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  assessmentId: z.string(),
  questionId: z.string(),
  status: ResponseStatusSchema,
  reviewState: ReviewStateSchema,
  currentVersion: z.number().int(),
  checklistComplete: z.boolean(),
  assignedTo: z.string().nullable(),
  sourceCorpusItemId: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  finalisedAt: z.string().nullable(),
  assessedAt: z.string().nullable(),
  lockedAt: z.string().nullable(),
});
export type ResponseRecord = z.infer<typeof ResponseRecordSchema>;

/** Row in the assessment response list (workspace navigation). */
export const ResponseListItemSchema = ResponseRecordSchema.extend({
  questionText: z.string(),
  responseType: QuestionSchema.shape.responseType,
  requiredEvidence: z.boolean(),
  clauseId: z.string(),
  clauseNumber: z.string(),
  clauseTitle: z.string(),
  evidenceCount: z.number().int(),
  openReviewerComments: z.number().int(),
  openAssessorComments: z.number().int(),
});
export type ResponseListItem = z.infer<typeof ResponseListItemSchema>;

/** `fields=summary` variant: same row without the bulky question text. */
export const ResponseSummaryItemSchema = ResponseListItemSchema.omit({ questionText: true });
export type ResponseSummaryItem = z.infer<typeof ResponseSummaryItemSchema>;

export const ResponseListFieldsSchema = z.enum(['full', 'summary']);
export type ResponseListFields = z.infer<typeof ResponseListFieldsSchema>;

export const ResponseVersionSchema = z.object({
  id: z.string(),
  responseId: z.string(),
  versionNumber: z.number().int(),
  content: z.string(),
  contentFormat: ContentFormatSchema,
  authorId: z.string(),
  authorName: z.string().optional(),
  changeSummary: z.string(),
  aiGenerated: z.boolean(),
  aiJobId: z.string().nullable(),
  createdAt: z.string(),
});
export type ResponseVersion = z.infer<typeof ResponseVersionSchema>;

export const ResponseDetailSchema = ResponseRecordSchema.extend({
  question: QuestionSchema,
  clause: ClauseSchema,
  content: ResponseVersionSchema.nullable(),
  evidenceCount: z.number().int(),
  openReviewerComments: z.number().int(),
  openAssessorComments: z.number().int(),
});
export type ResponseDetail = z.infer<typeof ResponseDetailSchema>;

export const SaveResponseContentInputSchema = z.object({
  content: z.string(),
  contentFormat: ContentFormatSchema.default('markdown'),
  changeSummary: z.string().max(1000).default(''),
  /** Present when accepting AI output — links the version to the ai_jobs row. */
  aiJobId: z.string().nullable().optional(),
});
export type SaveResponseContentInput = z.infer<typeof SaveResponseContentInputSchema>;

export const TransitionInputSchema = z.object({
  action: ResponseTransitionSchema,
  note: z.string().max(2000).optional(),
});
export type TransitionInput = z.infer<typeof TransitionInputSchema>;

export const ChecklistInputSchema = z.object({
  complete: z.boolean(),
});
export type ChecklistInput = z.infer<typeof ChecklistInputSchema>;

export const ResponseDiffSchema = z.object({
  responseId: z.string(),
  fromVersion: z.number().int(),
  toVersion: z.number().int(),
  /** Unified diff of the two versions' content. */
  unifiedDiff: z.string(),
});
export type ResponseDiff = z.infer<typeof ResponseDiffSchema>;
